<script>
import bem from '@/utils/bem';
import { mapActions } from 'vuex';
import { UploadOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons-vue';
import UploadImages from '@/components/ux/upload/Upload-images.vue';
import URLS from '@/store/urls';
import apiCall from '@/store/apiCall';
import notificationWithIcon from '@/utils/notification';

const b = bem('media-slider-modal');

export default {
  components: {
    UploadImages,
    UploadOutlined,
    DeleteOutlined,
    PlusOutlined,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    slides: {
      type: Array,
      default: () => [],
    },
    isShowVideo: {
      type: Boolean,
      default: false,
    },
    videoUrl: {
      type: String,
      default: '',
    },
    singleMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'save'],
  data() {
    return {
      mediaType: 'images',
      localVideoUrl: '',
      localSlides: [],
      currentSlideIndex: 0,
      desktopFiles: [],
      mobileFiles: [],
      isSaving: false,
      isVideoUploading: false,
    };
  },
  computed: {
    currentSlide() {
      return this.localSlides[this.currentSlideIndex] || {};
    },
    slideOptions() {
      return this.localSlides.map((_, index) => ({
        label: `Slide ${index + 1}`,
        value: index,
      }));
    },
  },
  watch: {
    visible: {
      handler(val) {
        if (val) {
          this.mediaType = this.isShowVideo ? 'video' : 'images';
          this.localVideoUrl = this.videoUrl || '';
          this.localSlides = this.slides && this.slides.length > 0
            ? JSON.parse(JSON.stringify(this.slides))
            : [{ imageUrl: '', imageUrlMobile: '' }];
          this.currentSlideIndex = 0;
          this.updateFilesForCurrentSlide();
        }
      },
      immediate: true,
    },
    currentSlideIndex() {
      this.updateFilesForCurrentSlide();
    },
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions({
      uploadModuleImagesAction: 'pageContent/uploadModuleImages',
    }),
    updateFilesForCurrentSlide() {
      const slide = this.currentSlide;
      this.desktopFiles = [];
      this.mobileFiles = [];
      this.$nextTick(() => {
        if (slide.imageUrl) {
          this.desktopFiles = [{ url: slide.imageUrl, id: 'desktop-initial' }];
        }
        if (slide.imageUrlMobile) {
          this.mobileFiles = [{ url: slide.imageUrlMobile, id: 'mobile-initial' }];
        }
      });
    },
    handleDesktopUpdate(files) {
      this.desktopFiles = files;
      if (files.length > 0) {
        this.localSlides[this.currentSlideIndex].imageUrl = files[0].url;
        this.localSlides[this.currentSlideIndex].desktopFile = files[0].file;
      } else {
        this.localSlides[this.currentSlideIndex].imageUrl = '';
        this.localSlides[this.currentSlideIndex].desktopFile = null;
      }
    },
    handleMobileUpdate(files) {
      this.mobileFiles = files;
      if (files.length > 0) {
        this.localSlides[this.currentSlideIndex].imageUrlMobile = files[0].url;
        this.localSlides[this.currentSlideIndex].mobileFile = files[0].file;
      } else {
        this.localSlides[this.currentSlideIndex].imageUrlMobile = '';
        this.localSlides[this.currentSlideIndex].mobileFile = null;
      }
    },
    addSlide() {
      this.localSlides.push({ imageUrl: '', imageUrlMobile: '' });
      this.currentSlideIndex = this.localSlides.length - 1;
    },
    deleteSlide() {
      if (this.localSlides.length > 1) {
        this.localSlides.splice(this.currentSlideIndex, 1);
        this.currentSlideIndex = Math.max(0, this.currentSlideIndex - 1);
      }
    },
    async loadVideo(info) {
      const { file } = info;
      if (/^video\//.test(file.type)) {
        this.isVideoUploading = true;
        notificationWithIcon('info', 'The video is being uploaded');
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('folder', 'header-videos');
        try {
          const {
            data: {
              data: { url: uploadedVideoUrl },
            },
          } = await apiCall({
            type: 'POST',
            headers: { 'content-type': 'multipart/form-data' },
            url: URLS.fileUpload,
            body: formData,
            isToken: true,
          });
          this.localVideoUrl = uploadedVideoUrl;
          notificationWithIcon('success', 'Video uploaded successfully!');
        } catch (err) {
          const message = err?.response?.data?.message || err?.message || 'Upload failed';
          notificationWithIcon('error', message);
        } finally {
          this.isVideoUploading = false;
        }
      } else {
        notificationWithIcon('error', 'File type is not correct. Please upload a video file.');
      }
    },
    async handleSave() {
      this.isSaving = true;
      try {
        const processedSlides = await Promise.all(
          this.localSlides.map(async (slide) => {
            let desktopUrl = slide.imageUrl;
            let mobileUrl = slide.imageUrlMobile;

            if (slide.desktopFile || slide.mobileFile) {
              const result = await this.uploadModuleImagesAction({
                desktopFile: slide.desktopFile || null,
                mobileFile: slide.mobileFile || null,
                currentImage: slide.desktopFile ? '' : slide.imageUrl,
                currentImageMobile: slide.mobileFile ? '' : slide.imageUrlMobile,
              });
              desktopUrl = result.image || desktopUrl;
              mobileUrl = result.imageMobile || mobileUrl;
            }

            return {
              id: slide.id,
              imageUrl: desktopUrl,
              imageUrlMobile: mobileUrl,
            };
          }),
        );

        this.$emit('save', {
          blocks: processedSlides,
          isShowVideo: this.mediaType === 'video',
          videoUrl: this.localVideoUrl,
        });
      } finally {
        this.isSaving = false;
      }
    },
    handleClose() {
      this.$emit('close');
    },
  },
};
</script>

<template>
  <a-modal
    :open="visible"
    :footer="null"
    width="800px"
    title="Edit Media"
    @cancel="handleClose"
  >
    <div :class="b()">
      <div :class="b('type-switch')">
        <a-radio-group v-model:value="mediaType" button-style="solid">
          <a-radio-button value="images">Images</a-radio-button>
          <a-radio-button value="video">Video</a-radio-button>
        </a-radio-group>
      </div>

      <template v-if="mediaType === 'images'">
        <div v-if="!singleMode" :class="b('slide-controls')">
          <a-select
            v-model:value="currentSlideIndex"
            :options="slideOptions"
            style="width: 150px"
          />
          <a-button type="primary" @click="addSlide">
            <PlusOutlined /> Add Slide
          </a-button>
          <a-button
            danger
            :disabled="localSlides.length <= 1"
            @click="deleteSlide"
          >
            <DeleteOutlined /> Delete Slide
          </a-button>
        </div>

        <div :class="b('images-row')">
          <div :class="b('image-field')">
            <span :class="b('label')">Desktop Image</span>
            <UploadImages
              :is-multiple="false"
              :initial-images="currentSlide.imageUrl ? [currentSlide.imageUrl] : []"
              :with-update-initial-images="true"
              @update:files="handleDesktopUpdate"
            />
          </div>
          <div :class="b('image-field')">
            <span :class="b('label')">Mobile Image</span>
            <UploadImages
              :is-multiple="false"
              :initial-images="currentSlide.imageUrlMobile ? [currentSlide.imageUrlMobile] : []"
              :with-update-initial-images="true"
              @update:files="handleMobileUpdate"
            />
          </div>
        </div>

        <div v-if="!singleMode" :class="b('slides-preview')">
          <span :class="b('label')">All Slides Preview:</span>
          <div :class="b('slides-list')">
            <div
              v-for="(slide, index) in localSlides"
              :key="index"
              :class="b('slide-thumb', { active: index === currentSlideIndex })"
              role="button"
              tabindex="0"
              @click="currentSlideIndex = index"
              @keyup.enter="currentSlideIndex = index"
              @keyup.space="currentSlideIndex = index"
            >
              <img
                v-if="slide.imageUrl"
                :src="slide.imageUrl"
                :alt="`Slide ${index + 1}`"
              />
              <div v-else :class="b('slide-empty')">{{ index + 1 }}</div>
            </div>
          </div>
        </div>
      </template>

      <template v-else>
        <div :class="b('video-section')">
          <div :class="b('video-input')">
            <span :class="b('label')">Video URL</span>
            <a-input
              v-model:value="localVideoUrl"
              placeholder="Enter video URL or upload a file"
            />
          </div>

          <div :class="b('video-upload')">
            <span :class="b('label')">Or upload video file</span>
            <a-upload
              name="video"
              :show-upload-list="false"
              :custom-request="loadVideo"
            >
              <a-button :loading="isVideoUploading">
                <UploadOutlined /> Upload Video
              </a-button>
            </a-upload>
          </div>

          <div v-if="localVideoUrl" :class="b('video-preview')">
            <span :class="b('label')">Preview:</span>
            <video
              :src="localVideoUrl"
              controls
              :class="b('video-player')"
            >
              <track kind="captions" />
            </video>
          </div>
        </div>
      </template>

      <div :class="b('actions')">
        <a-button @click="handleClose">Cancel</a-button>
        <a-button type="primary" :loading="isSaving" @click="handleSave">
          Save
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<style scoped lang="scss">
@import '@/styles/variables';
@import '@/styles/media';

.media-slider-modal {
  &__type-switch {
    margin-bottom: 24px;
    display: flex;
    justify-content: center;

    @include mediaMinMax(1025, $normal) {
      margin-bottom: vw-full(24);
    }
  }

  &__slide-controls {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(12);
      margin-bottom: vw-full(24);
    }
  }

  &__images-row {
    display: flex;
    gap: 24px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(24);
    }
  }

  &__image-field {
    flex: 1;
    margin-bottom: 16px;

    @include mediaMinMax(1025, $normal) {
      margin-bottom: vw-full(16);
    }
  }

  &__label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #293241;
    margin-bottom: 8px;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(14);
      margin-bottom: vw-full(8);
    }
  }

  &__slides-preview {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #e0e0e0;

    @include mediaMinMax(1025, $normal) {
      margin-top: vw-full(24);
      padding-top: vw-full(24);
    }
  }

  &__slides-list {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(12);
    }
  }

  &__slide-thumb {
    width: 80px;
    height: 60px;
    border: 2px solid #d9d9d9;
    border-radius: 4px;
    overflow: hidden;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f5f5;
    transition: all 0.2s;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(80);
      height: vw-full(60);
      border-radius: vw-full(4);
    }

    &:hover {
      border-color: #1890ff;
    }

    &_active {
      border-color: #1890ff;
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__slide-empty {
    font-size: 18px;
    font-weight: 600;
    color: #999;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(18);
    }
  }

  &__video-section {
    display: flex;
    flex-direction: column;
    gap: 24px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(24);
    }
  }

  &__video-input,
  &__video-upload {
    display: flex;
    flex-direction: column;
    gap: 8px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(8);
    }
  }

  &__video-preview {
    margin-top: 16px;

    @include mediaMinMax(1025, $normal) {
      margin-top: vw-full(16);
    }
  }

  &__video-player {
    width: 100%;
    max-height: 300px;
    border-radius: 8px;

    @include mediaMinMax(1025, $normal) {
      max-height: vw-full(300);
      border-radius: vw-full(8);
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #e0e0e0;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(12);
      margin-top: vw-full(24);
      padding-top: vw-full(24);
    }
  }
}

:deep(.ant-upload-select-picture-card) {
  width: 128px !important;
  height: 128px !important;
}

:deep(.ant-upload-list-picture-card-container) {
  width: 128px !important;
  height: 128px !important;
}
</style>
